import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faBan, faCheckCircle, faExclamationTriangle, faExternalLinkAlt,
  faFileExcel, faFilePdf, faInfoCircle, faMoneyCheckAlt, faSync,
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';
import Footer from '../../components/ui/Footer';
import logo from '../../assets/images/IMT_logo.png';
import SupplierPdfChooserModal from '../../components/shared/SupplierPdfChooserModal';
import { useFlashMessage } from '../../components/FlashMessage';
import { extractApiError } from '../../utils/errorMessages';
import {
  cancelPaymentRun, createPaymentRun, downloadPaymentRunExcel,
  fetchPaymentRunCompanies, listPaymentRuns, previewPaymentRun,
} from '../../services/paymentRunService';
import './OutgoingInvoice.css';
import './PaymentRunPage.css';
import { accountsForPledges, pledgeBlockers, pledgeHint, pledgesOfRows } from '../../utils/purchasePledge';

/**
 * Payment Run (ClickUp "Payment run", ledger row 120).
 *
 * Pick one of our companies and a date -> the approved supplier invoices due by
 * then -> tick what to pay, choose which of the company's own accounts pays ->
 * the run is saved and the Excel file for accounting downloads.
 *
 * The screen decides nothing about money. What is payable, why something is
 * not, which companies this person may choose and which accounts may pay all
 * come from the server, and the server checks everything again when the run is
 * created. What the screen owns is saying WHY: an empty list names the invoices
 * still waiting for approval, a load failure is a banner and never an empty
 * table, and a refused run lists every invoice that stopped it.
 */

const pad = (n) => String(n).padStart(2, '0');
export const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Spelled out, not left to the browser: en-GB prints "Sept" in some browsers and
// "Sep" in others, and the server's own sentences ("not due until 10 Sep 2026")
// must read the same as the table beside them.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-09-10" -> "10 Sep 2026", read as a calendar date (no timezone shift). */
export const formatDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d || m > 12) return String(iso);
  return `${pad(d)} ${MONTHS[m - 1]} ${y}`;
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return iso;
  return `${pad(when.getDate())} ${MONTHS[when.getMonth()]} ${when.getFullYear()} ${pad(when.getHours())}:${pad(when.getMinutes())}`;
};

export const formatMoney = (amount) => {
  const n = Number(amount);
  return Number.isNaN(n) ? String(amount) : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Per-currency totals for the ticked rows. */
export const totalsFor = (rows) => {
  const totals = {};
  rows.forEach((row) => {
    const entry = totals[row.currency] || { amount: 0, count: 0 };
    // Cents as integers: summing floats drifts a cent on long lists.
    entry.amount += Math.round(Number(row.amount_due) * 100);
    entry.count += 1;
    totals[row.currency] = entry;
  });
  return Object.keys(totals).sort().map((currency) => ({
    currency, amount: (totals[currency].amount / 100).toFixed(2), count: totals[currency].count,
  }));
};

const totalsText = (totals) => totals.map((t) => `${t.currency} ${formatMoney(t.amount)}`).join(' · ');

/** The account the box may fill in by itself -- the server's rule: one main, or the only one. */
export const autoPickAccount = (accounts) => {
  const mains = (accounts || []).filter((a) => a.is_main);
  if (mains.length === 1) return mains[0];
  if ((accounts || []).length === 1 && mains.length === 0) return accounts[0];
  return null;
};

const bankLine = (bank) => [bank.iban || bank.account_number, bank.swift || bank.bic].filter(Boolean).join(' · ');

const Notice = ({ tone = 'info', icon = faInfoCircle, children, role }) => (
  <div className={`pr-notice pr-notice-${tone}`} role={role}>
    <FontAwesomeIcon icon={icon} className="pr-notice-icon" />
    <div>{children}</div>
  </div>
);

const BankCell = ({ row }) => {
  if (!row.bank) return <span className="pr-muted">—</span>;
  return (
    <div className="pr-bank">
      <div className="pr-bank-name">{row.bank.bank_name || '(bank name missing)'}</div>
      <div className="pr-bank-line">{bankLine(row.bank)}</div>
      {row.bank.account_owner && <div className="pr-bank-owner">{row.bank.account_owner}</div>}
      {row.bank_source !== 'CHOSEN_ON_INVOICE' && row.bank_source_label && (
        <div className="pr-bank-source">{row.bank_source_label}</div>
      )}
    </div>
  );
};

const InvoiceNumbers = ({ row }) => (
  <>
    <div className="pr-strong">{row.supplier_invoice_number || '—'}</div>
    <div className="pr-muted">{row.hms_expense_number}</div>
  </>
);

const RowActions = ({ row, onView }) => (
  <div className="pr-row-actions">
    <button type="button" className="pr-link-button" onClick={() => onView(row)}>
      <FontAwesomeIcon icon={faFilePdf} /> View
    </button>
    <a className="pr-link-button" href={`/invoicing/incoming/${row.id}`} target="_blank" rel="noopener noreferrer">
      <FontAwesomeIcon icon={faExternalLinkAlt} /> Open
    </a>
  </div>
);

const PaymentRunPage = () => {
  const history = useHistory();
  const { showSuccess, showError } = useFlashMessage();

  const [companies, setCompanies] = useState([]);
  const [companiesError, setCompaniesError] = useState(null);
  const [fixedTo, setFixedTo] = useState(null);
  const [scopeWarning, setScopeWarning] = useState(null);

  const [companyId, setCompanyId] = useState('');
  const [runDate, setRunDate] = useState(todayIso());
  const [costCategory, setCostCategory] = useState('');

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const [selected, setSelected] = useState(() => new Set());
  const [payingAccountId, setPayingAccountId] = useState('');
  const [notes, setNotes] = useState('');

  const [confirming, setConfirming] = useState(false);
  const [creating, setCreating] = useState(false);
  const [refusal, setRefusal] = useState(null);

  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [runsError, setRunsError] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const [pdfInvoiceId, setPdfInvoiceId] = useState(null);
  // Which company+date the list on screen belongs to. A refresh of the same
  // list keeps the person's ticks and account; a new company or date starts over.
  const loadedKey = useRef(null);
  const loadedReady = useRef(null);

  const loadCompanies = useCallback(async () => {
    setCompaniesError(null);
    try {
      const data = await fetchPaymentRunCompanies();
      const list = data.results || [];
      setCompanies(list);
      setFixedTo(data.fixed_to || null);
      setScopeWarning(data.scope_warning || null);
      // Chosen for the person only when there is nothing to choose.
      if (data.fixed_to) setCompanyId(String(data.fixed_to));
      else if (list.length === 1) setCompanyId(String(list[0].id));
    } catch (error) {
      setCompaniesError(extractApiError(error, 'The list of companies could not be loaded.'));
    }
  }, []);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    setRunsError(null);
    try {
      const data = await listPaymentRuns();
      setRuns(data.results || []);
    } catch (error) {
      setRunsError(extractApiError(error, 'Previous payment runs could not be loaded.'));
    } finally {
      setRunsLoading(false);
    }
  }, []);

  const loadPreview = useCallback(async () => {
    if (!companyId || !runDate) {
      setPreview(null);
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const data = await previewPaymentRun(companyId, runDate, costCategory);
      // The category is part of the list's identity: a new filter is a new list, so
      // nothing stays ticked that the person can no longer see.
      const key = `${companyId}|${runDate}|${costCategory}`;
      const sameList = loadedKey.current === key;
      loadedKey.current = key;
      const readyIds = (data.ready || []).map((row) => row.id);
      const knownBefore = new Set(sameList ? (loadedReady.current || []).map((row) => row.id) : []);
      loadedReady.current = data.ready || [];
      setPreview(data);
      // Same list refreshed: keep what was unticked, tick what is new.
      setSelected((prevSelected) => new Set(readyIds.filter(
        (id) => !sameList || !knownBefore.has(id) || prevSelected.has(id),
      )));
      const accounts = data.paying_accounts || [];
      const picked = autoPickAccount(accounts);
      setPayingAccountId((prev) => (
        sameList && accounts.some((a) => String(a.id) === String(prev))
          ? prev
          : (picked ? String(picked.id) : '')
      ));
    } catch (error) {
      setPreview(null);
      setPreviewError(extractApiError(error, 'The invoices for this company could not be loaded.'));
    } finally {
      setPreviewLoading(false);
    }
  }, [companyId, runDate, costCategory]);

  useEffect(() => { loadCompanies(); loadRuns(); }, [loadCompanies, loadRuns]);
  useEffect(() => { setRefusal(null); loadPreview(); }, [loadPreview]);

  const ready = useMemo(() => preview?.ready || [], [preview]);
  const blocked = preview?.blocked || [];
  const tickedRows = useMemo(() => ready.filter((row) => selected.has(row.id)), [ready, selected]);
  const tickedTotals = useMemo(() => totalsFor(tickedRows), [tickedRows]);
  // A pledged purchase is paid from the pledge bank only (Chris, 2026-10-23).
  const pledges = useMemo(() => pledgesOfRows(tickedRows), [tickedRows]);
  const payingAccounts = accountsForPledges(preview?.paying_accounts || [], pledges);
  const payingAccount = payingAccounts.find((a) => String(a.id) === String(payingAccountId)) || null;
  const companyName = companies.find((c) => String(c.id) === String(companyId))?.name || preview?.hms_entity?.name || '';

  // The ticks can narrow the accounts: drop a chosen account the pledge no longer allows.
  useEffect(() => {
    if (payingAccounts.some((a) => String(a.id) === String(payingAccountId))) return;
    const picked = autoPickAccount(payingAccounts);
    const next = picked ? String(picked.id) : '';
    if (next !== payingAccountId) setPayingAccountId(next);
  }, [payingAccounts, payingAccountId]);

  const blockers = [];
  if (preview?.paying_accounts_problem) blockers.push(preview.paying_accounts_problem);
  else if (!payingAccount) blockers.push('Choose which bank account pays.');
  blockers.push(...pledgeBlockers(pledges, payingAccounts, preview?.paying_accounts_owner || companyName));
  if (tickedRows.length === 0) blockers.push('Tick at least one invoice to pay.');

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allTicked = ready.length > 0 && tickedRows.length === ready.length;
  const toggleAll = () => setSelected(allTicked ? new Set() : new Set(ready.map((row) => row.id)));

  const download = async (run) => {
    setDownloadingId(run.id);
    try {
      await downloadPaymentRunExcel(run);
    } catch (error) {
      showError(extractApiError(error, `The Excel file for ${run.reference} could not be downloaded.`));
    } finally {
      setDownloadingId(null);
    }
  };

  const create = async () => {
    setCreating(true);
    setRefusal(null);
    try {
      const run = await createPaymentRun({
        hms_entity: Number(companyId),
        run_date: runDate,
        paying_bank_account: Number(payingAccountId),
        invoice_ids: tickedRows.map((row) => row.id),
        notes,
      });
      setConfirming(false);
      setNotes('');
      showSuccess(`Payment run ${run.reference} created. The Excel file is downloading.`);
      await download(run);
      loadRuns();
      loadPreview();
    } catch (error) {
      setConfirming(false);
      const data = error?.response?.data;
      setRefusal({
        message: extractApiError(error, 'The payment run could not be created.'),
        problems: Array.isArray(data?.problems) ? data.problems : [],
      });
      // What was payable a minute ago may not be now: show the current truth.
      loadPreview();
    } finally {
      setCreating(false);
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const data = await cancelPaymentRun(cancelTarget.id);
      showSuccess(data.message || `${cancelTarget.reference} cancelled.`);
      setCancelTarget(null);
      loadRuns();
      loadPreview();
    } catch (error) {
      showError(extractApiError(error, `${cancelTarget.reference} could not be cancelled.`));
    } finally {
      setCancelling(false);
    }
  };

  const viewPdf = (row) => setPdfInvoiceId(row.id);

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="invoice-page-container incoming-invoice-screen payment-run-screen">
        <div className="hms-screen-header">
          <button
            className="invoice-header-back-button"
            onClick={() => history.push('/invoicing/incoming')}
            title="Back to Incoming Invoices"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div className="header-logo">
            <img src={logo} alt="IMT" className="header-logo-img" />
            <div className="header-title-content">
              <h1 className="hms-screen-title">PAYMENT RUN</h1>
              <p className="invoice-subtitle">Approved supplier invoices due for payment</p>
            </div>
          </div>
        </div>

        <div className="pr-body">
          {/* 1. Company and date */}
          <section className="pr-card pr-controls" aria-label="Company and date">
            <label className="pr-field">
              <span className="pr-label">IMT company</span>
              <select
                value={companyId}
                onChange={(e) => { setCompanyId(e.target.value); setCostCategory(''); }}
                disabled={Boolean(fixedTo)}
                aria-label="IMT company"
              >
                {!fixedTo && <option value="">Choose a company…</option>}
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {fixedTo && <span className="pr-hint">You keep this company's books.</span>}
            </label>
            <label className="pr-field">
              <span className="pr-label">Date of payment run</span>
              <input type="date" value={runDate} onChange={(e) => setRunDate(e.target.value)} aria-label="Date of payment run" />
              <span className="pr-hint">Invoices due on or before this date.</span>
            </label>
            <label className="pr-field">
              <span className="pr-label">Cost category</span>
              <select
                value={costCategory}
                onChange={(e) => setCostCategory(e.target.value)}
                disabled={!companyId}
                aria-label="Cost category"
              >
                <option value="">All categories</option>
                {(preview?.categories || []).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                <option value="none">No category</option>
              </select>
              <span className="pr-hint">Only invoices in this category.</span>
            </label>
            <button type="button" className="btn-secondary pr-reload" onClick={loadPreview} disabled={!companyId || previewLoading}>
              <FontAwesomeIcon icon={faSync} spin={previewLoading} /> Refresh
            </button>
          </section>

          {companiesError && <Notice tone="error" icon={faExclamationTriangle} role="alert">{companiesError}</Notice>}
          {scopeWarning && <Notice tone="warn" icon={faExclamationTriangle}>{scopeWarning}</Notice>}

          {!companyId && !companiesError && (
            <Notice>Choose a company to see its supplier invoices due for payment.</Notice>
          )}

          {previewError && (
            <div className="pr-notice pr-notice-error" role="alert">
              <FontAwesomeIcon icon={faExclamationTriangle} className="pr-notice-icon" />
              <div>
                <strong>Nothing could be loaded.</strong> {previewError}
              </div>
              <button type="button" className="btn-secondary" onClick={loadPreview}>Try again</button>
            </div>
          )}

          {companyId && previewLoading && !preview && (
            <div className="pr-card pr-loading">Loading supplier invoices…</div>
          )}

          {preview && (
            <>
              {/* 2. Ready to pay */}
              <section className="pr-card" aria-label="Ready to pay">
                <div className="pr-card-head">
                  <h2>
                    <FontAwesomeIcon icon={faCheckCircle} className="pr-ok" /> Ready to pay
                    <span className="pr-count">{ready.length}</span>
                  </h2>
                  <span className="pr-muted">{companyName} · due by {formatDate(preview.run_date)}</span>
                </div>

                {preview.not_approved_count > 0 && (
                  <Notice>
                    {preview.not_approved_count} more invoice{preview.not_approved_count === 1 ? '' : 's'} due by
                    this date (or with no due date) {preview.not_approved_count === 1 ? "isn't" : "aren't"} approved
                    yet, so {preview.not_approved_count === 1 ? "it can't" : "they can't"} be paid.
                  </Notice>
                )}

                {ready.length === 0 ? (
                  <div className="pr-empty">
                    No approved supplier invoices for {companyName} are due by {formatDate(preview.run_date)}.
                  </div>
                ) : (
                  <div className="pr-table-wrap">
                    <table className="pr-table">
                      <thead>
                        <tr>
                          <th className="pr-check">
                            <input type="checkbox" checked={allTicked} onChange={toggleAll} aria-label="Tick all invoices" />
                          </th>
                          <th>Supplier</th>
                          <th>Invoice number</th>
                          <th>Due date</th>
                          <th>Cost category</th>
                          <th className="pr-num">Amount due</th>
                          <th>Currency</th>
                          <th>Bank details (supplier)</th>
                          <th>Invoice</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ready.map((row) => (
                          <tr key={row.id} className={selected.has(row.id) ? 'pr-row-ticked' : ''}>
                            <td className="pr-check">
                              <input
                                type="checkbox"
                                checked={selected.has(row.id)}
                                onChange={() => toggle(row.id)}
                                aria-label={`Pay ${row.supplier_name} ${row.supplier_invoice_number || row.hms_expense_number}`}
                              />
                            </td>
                            <td className="pr-strong">
                              {row.supplier_name}
                              {row.pledge && <div className="pr-pledge-tag" title="Paid from this bank only">Pledged to {row.pledge.name}</div>}
                            </td>
                            <td><InvoiceNumbers row={row} /></td>
                            <td className={row.is_overdue ? 'pr-overdue' : ''}>
                              {formatDate(row.due_date)}
                              {row.is_overdue && <div className="pr-overdue-tag">Overdue</div>}
                            </td>
                            <td>{row.cost_category?.label || '—'}</td>
                            <td className="pr-num pr-strong">{formatMoney(row.amount_due)}</td>
                            <td>{row.currency}</td>
                            <td><BankCell row={row} /></td>
                            <td><RowActions row={row} onView={viewPdf} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* 3. Can't be paid yet */}
              {blocked.length > 0 && (
                <section className="pr-card" aria-label="Can't be paid yet">
                  <div className="pr-card-head">
                    <h2>
                      <FontAwesomeIcon icon={faExclamationTriangle} className="pr-warn" /> Can't be paid yet
                      <span className="pr-count pr-count-warn">{blocked.length}</span>
                    </h2>
                    <span className="pr-muted">Fix these on the invoice or in CRM, then refresh.</span>
                  </div>
                  <div className="pr-table-wrap">
                    <table className="pr-table">
                      <thead>
                        <tr>
                          <th>Supplier</th>
                          <th>Invoice number</th>
                          <th>Due date</th>
                          <th className="pr-num">Amount due</th>
                          <th>Currency</th>
                          <th>Why not</th>
                          <th>Invoice</th>
                        </tr>
                      </thead>
                      <tbody>
                        {blocked.map((row) => (
                          <tr key={row.id}>
                            <td className="pr-strong">{row.supplier_name}</td>
                            <td><InvoiceNumbers row={row} /></td>
                            <td>{formatDate(row.due_date)}</td>
                            <td className="pr-num">{formatMoney(row.amount_due)}</td>
                            <td>{row.currency}</td>
                            <td>
                              <ul className="pr-reasons">
                                {row.reasons.map((reason) => <li key={reason}>{reason}</li>)}
                              </ul>
                            </td>
                            <td><RowActions row={row} onView={viewPdf} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* 4. Pay from + create */}
              <section className="pr-card pr-pay" aria-label="Create the payment run">
                <div className="pr-card-head">
                  <h2><FontAwesomeIcon icon={faMoneyCheckAlt} /> Create the payment run</h2>
                </div>
                <div className="pr-pay-grid">
                  <label className="pr-field">
                    <span className="pr-label">Pay from ({preview.paying_accounts_owner || companyName})</span>
                    {preview.paying_accounts_problem ? (
                      <Notice tone="warn" icon={faExclamationTriangle}>{preview.paying_accounts_problem}</Notice>
                    ) : (
                      <select
                        value={payingAccountId}
                        onChange={(e) => setPayingAccountId(e.target.value)}
                        aria-label="Pay from bank account"
                      >
                        {!autoPickAccount(payingAccounts) && <option value="">Choose the bank account that pays…</option>}
                        {payingAccounts.map((a) => (
                          <option key={a.id} value={a.id}>{a.label}</option>
                        ))}
                      </select>
                    )}
                    {pledgeHint(pledges) && <span className="pr-hint">{pledgeHint(pledges)}</span>}
                    {payingAccount && <span className="pr-hint">{bankLine(payingAccount)}</span>}
                  </label>
                  <label className="pr-field">
                    <span className="pr-label">Notes for accounting (optional)</span>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={2000} />
                  </label>
                </div>

                <div className="pr-summary">
                  <div>
                    <div className="pr-summary-count">
                      {tickedRows.length} of {ready.length} invoice{ready.length === 1 ? '' : 's'} ticked
                    </div>
                    <div className="pr-summary-totals">{tickedTotals.length ? totalsText(tickedTotals) : '—'}</div>
                  </div>
                  <div className="pr-summary-action">
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={blockers.length > 0 || creating}
                      onClick={() => setConfirming(true)}
                    >
                      <FontAwesomeIcon icon={faFileExcel} /> Create payment run &amp; download Excel
                    </button>
                    {blockers.length > 0 && <div className="pr-blockers">{blockers.join(' ')}</div>}
                  </div>
                </div>

                {refusal && (
                  <div className="pr-notice pr-notice-error" role="alert">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="pr-notice-icon" />
                    <div>
                      <strong>{refusal.message}</strong>
                      {refusal.problems.length > 0 && (
                        <ul className="pr-reasons">
                          {refusal.problems.map((p) => (
                            <li key={p.invoice_id}><strong>{p.label}:</strong> {p.reasons.join(' ')}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </section>
            </>
          )}

          {/* 5. Previous runs */}
          <section className="pr-card" aria-label="Payment runs">
            <div className="pr-card-head">
              <h2>Payment runs</h2>
              <button type="button" className="btn-secondary" onClick={loadRuns} disabled={runsLoading}>
                <FontAwesomeIcon icon={faSync} spin={runsLoading} /> Refresh
              </button>
            </div>
            {runsError ? (
              <div className="pr-notice pr-notice-error" role="alert">
                <FontAwesomeIcon icon={faExclamationTriangle} className="pr-notice-icon" />
                <div><strong>Nothing could be loaded.</strong> {runsError}</div>
                <button type="button" className="btn-secondary" onClick={loadRuns}>Try again</button>
              </div>
            ) : runsLoading && runs.length === 0 ? (
              <div className="pr-empty">Loading payment runs…</div>
            ) : runs.length === 0 ? (
              <div className="pr-empty">No payment runs yet.</div>
            ) : (
              <div className="pr-table-wrap">
                <table className="pr-table">
                  <thead>
                    <tr>
                      <th>Run</th>
                      <th>Company</th>
                      <th>Run date</th>
                      <th>Pay from</th>
                      <th className="pr-num">Invoices</th>
                      <th>Totals</th>
                      <th>Created</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((run) => (
                      <tr key={run.id} className={run.status === 'CANCELLED' ? 'pr-row-cancelled' : ''}>
                        <td className="pr-strong">{run.reference}</td>
                        <td>{run.hms_entity?.name}</td>
                        <td>{formatDate(run.run_date)}</td>
                        <td>
                          <div>{run.paying_account?.bank_name}</div>
                          <div className="pr-muted">{run.paying_account && bankLine(run.paying_account)}</div>
                        </td>
                        <td className="pr-num">{run.invoice_count}</td>
                        <td>{totalsText(run.totals_by_currency || [])}</td>
                        <td>
                          <div>{run.created_by_name}</div>
                          <div className="pr-muted">{formatDateTime(run.created_at)}</div>
                        </td>
                        <td>
                          <span className={`pr-status pr-status-${run.status.toLowerCase()}`}>{run.status_display}</span>
                          {run.status === 'CANCELLED' && (
                            <div className="pr-muted">{run.cancelled_by_name} · {formatDateTime(run.cancelled_at)}</div>
                          )}
                        </td>
                        <td className="pr-run-actions">
                          <button type="button" className="pr-link-button" onClick={() => download(run)} disabled={downloadingId === run.id}>
                            <FontAwesomeIcon icon={faFileExcel} /> {downloadingId === run.id ? 'Downloading…' : 'Excel'}
                          </button>
                          {run.status === 'OPEN' && (
                            <button type="button" className="pr-link-button pr-danger" onClick={() => setCancelTarget(run)}>
                              <FontAwesomeIcon icon={faBan} /> Cancel run
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
        <Footer />
      </div>

      {confirming && (
        <div className="pr-modal-backdrop" role="presentation">
          <div className="pr-modal" role="dialog" aria-modal="true" aria-label="Confirm payment run">
            <h3>Create this payment run?</h3>
            <dl className="pr-modal-facts">
              <dt>Company</dt><dd>{companyName}</dd>
              <dt>Run date</dt><dd>{formatDate(runDate)}</dd>
              <dt>Invoices</dt><dd>{tickedRows.length}</dd>
              <dt>To pay</dt><dd className="pr-strong">{totalsText(tickedTotals)}</dd>
              <dt>Pay from</dt><dd>{payingAccount?.label}</dd>
            </dl>
            <p className="pr-muted">
              These invoices will show as <strong>Scheduled</strong> and can't go on another run unless this one is
              cancelled. Nothing is marked as paid: record the payment as usual once the money has gone.
            </p>
            <div className="pr-modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setConfirming(false)} disabled={creating}>
                Go back
              </button>
              <button type="button" className="btn-primary" onClick={create} disabled={creating}>
                <FontAwesomeIcon icon={faFileExcel} /> {creating ? 'Creating…' : 'Create run & download Excel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelTarget && (
        <div className="pr-modal-backdrop" role="presentation">
          <div className="pr-modal" role="dialog" aria-modal="true" aria-label="Confirm cancelling the payment run">
            <h3>Cancel {cancelTarget.reference}?</h3>
            <p>
              Its {cancelTarget.invoice_count} invoice{cancelTarget.invoice_count === 1 ? '' : 's'} stop showing as
              Scheduled and can go on a new run (any already paid stay paid). The run and its Excel file are kept,
              marked cancelled. Only cancel if accounting has not paid from this list.
            </p>
            <div className="pr-modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setCancelTarget(null)} disabled={cancelling}>
                Keep the run
              </button>
              <button type="button" className="btn-primary pr-danger-button" onClick={confirmCancel} disabled={cancelling}>
                <FontAwesomeIcon icon={faBan} /> {cancelling ? 'Cancelling…' : 'Cancel run'}
              </button>
            </div>
          </div>
        </div>
      )}

      <SupplierPdfChooserModal
        isOpen={pdfInvoiceId !== null}
        invoiceId={pdfInvoiceId}
        onClose={() => setPdfInvoiceId(null)}
        showError={showError}
      />
    </Layout>
  );
};

export default PaymentRunPage;
